import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { NewsletterSummary } from '../../../types/models/Newsletter';
import type { ApiResponseNewsletterDelete } from '../../../types/api/ApiResponseNewsletter';

interface NewsletterDeleteModalProps {
  newsletter: NewsletterSummary;
  onClose: () => void;
  /** Drop the row from the current view (it was hard-deleted, or archived out of the active set). */
  onCompleted: (id: number) => void;
}

/**
 * Confirm-and-delete modal implementing the backend's delete STATE MACHINE:
 *   • a not-sent newsletter (draft / queued / failed) is hard-deleted (a queued one's send is
 *     cancelled first);
 *   • a SENT, not-yet-archived newsletter is ARCHIVED rather than destroyed — it must pass through
 *     the archived state before it can be removed;
 *   • an already-archived newsletter is hard-deleted (the deliberate second step).
 *
 * The controller decides based on the row's state and returns `action: 'deleted' | 'archived'`; the
 * copy + button here are pre-computed from the same rule so they match the outcome. Either way the
 * row leaves the current view, so the parent drops it by id.
 */
function NewsletterDeleteModal({ newsletter, onClose, onCompleted }: NewsletterDeleteModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  // Will the server archive (soft) or hard-delete? Mirrors NewsletterController::delete().
  const willArchive = newsletter.is_sent && !newsletter.is_archived;

  const handleConfirm = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/${newsletter.id}`, { method: 'DELETE' });
      // Branch on the JSend `body.status`, NEVER `response.ok`.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterDelete | null;

      if (body?.status === 'success' && (body.data?.action === 'deleted' || body.data?.action === 'archived')) {
        const archived = body.data.action === 'archived';
        addNotification({
          title: archived ? 'Newsletter archived' : 'Newsletter deleted',
          srTitle: 'Success alert',
          description: archived
            ? `${newsletter.subject} was archived and kept as history.`
            : `${newsletter.subject} was permanently deleted.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onCompleted(newsletter.id);
        onClose();
        return;
      }

      const fieldMessage = typeof body?.data?.newsletter === 'string' ? body.data.newsletter : null;
      const message = fieldMessage ?? body?.message ?? 'Could not delete the newsletter. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not delete newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not delete newsletter',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose}>
      <ModalHeader title={willArchive ? 'Archive newsletter' : 'Delete newsletter'} titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title={willArchive ? 'Archive failed' : 'Delete failed'} className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {willArchive ? (
          <Content>
            Archive <strong>{newsletter.subject}</strong>? A sent newsletter is history, so it is kept and moved to the
            archived set instead of being destroyed. You can permanently delete it from the Archived view.
          </Content>
        ) : (
          <Content>
            Permanently delete <strong>{newsletter.subject}</strong>?
            {newsletter.status === 'queued' && ' This cancels the pending send before the worker picks it up.'}
            {newsletter.is_archived
              ? ' This archived newsletter will be removed for good — this cannot be undone.'
              : ' This cannot be undone.'}
          </Content>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant={willArchive ? 'primary' : 'danger'}
          onClick={() => void handleConfirm()}
          isLoading={isWorking}
          isDisabled={isWorking}
        >
          {willArchive ? 'Archive' : 'Delete'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isWorking}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NewsletterDeleteModal;
